// #region imports

// --- Angular
import {
  Component,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

// --- RxJs
import { switchMap, map } from 'rxjs/operators';
import { Subject, Subscription, timer } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { User as OidcUser } from 'oidc-client';

// --- HS Code
import { NavBarBaseComponent } from '../../../core/nav-bar/nav-bar-base.component';
import {
  NotificationModel,
  NotificationTypes,
  UserDetailsModel,
} from '../../../models';

// #endregion

@Component({
  selector: 'hs-dashboard-top-nav',
  templateUrl: './dashboard-top-nav.component.html',
  styleUrls: ['./dashboard-top-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardTopNavComponent
  extends NavBarBaseComponent
  implements OnInit, OnDestroy, OnChanges
{
  readonly defaultMenuAvatarDiameter: Number = 24;

  @Input() hasNotifications: boolean | null = false;
  @Input() notifications: NotificationModel[] | null = new Array<NotificationModel>();
  @Input() markAsReadLabel: string = '';
  @Input() downloadLabel: string = '';
  @Input() isParticipant: boolean = false;
  @Input() numberOfNewNotifications: number | null = 0;
  @Input() reportIsDownLoading: boolean | null = false;
  @Input() showDisclaimerCopyrightLink: boolean = false;
  @Input() customTitle: string = '';
  @Input() userDetails: UserDetailsModel | null = null;
  @Input() user: OidcUser | null = null;

  @Output() notificationClicked = new EventEmitter();
  @Output() showProfileClicked = new EventEmitter();
  @Output() viewNotification = new EventEmitter<NotificationModel>();
  @Output() removeNotification = new EventEmitter<NotificationModel>();
  @Output() disclaimerCopyrightClick = new EventEmitter();

  private readonly _oneMinuteInMilliSeconds: number = 60000;

  hasLanguages: boolean = false;
  notificationTypes = NotificationTypes;
  timeOfDay: string;
  timeOfDaySubscription: Subscription = Subscription.EMPTY;
  languageChangedSubscription: Subscription = Subscription.EMPTY;
  timeOfDaySubject = new Subject<string>();

  isRespondent: boolean = false;

  constructor(elementRef: ElementRef, private translate: TranslateService) {
    super(elementRef);
    this.timeOfDay = '';
  }

  ngOnInit() {
    this.timeOfDaySubscription = timer(0, this._oneMinuteInMilliSeconds)
      .pipe(
        map(() => this._getTimeKey()),
        switchMap((timeKey) => this.translate.get(timeKey)),
        switchMap((timeOfDay) => {
          let firstName = '';
          if (this.userDetails && this.userDetails.firstName) {
            firstName = this.userDetails.firstName;
          }
          return this.translate.get('greeting', { name: firstName, timeOfDay });
        })
      )
      .subscribe((text) => {
        this.timeOfDaySubject.next(text);
      });

    this.languageChangedSubscription = this.translate.onLangChange
      .pipe(
        map(() => this._getTimeKey()),
        switchMap((timeKey) => this.translate.get(timeKey)),
        switchMap((timeOfDay) => {
          let firstName = '';
          if (this.userDetails && this.userDetails.firstName) {
            firstName = this.userDetails.firstName;
          }
          return this.translate.get('greeting', { name: firstName, timeOfDay });
        })
      )
      .subscribe((text) => {
        this.timeOfDay = text;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['languages'] && changes['languages'].currentValue) {
      this.hasLanguages = (this.languages?.length || 0) > 0;
    }
    if (changes && changes['user'] && changes['user'].currentValue) {
      if (this.user && this.user.profile) {
        if (this.user.profile['surveyparticipant']) {
          this.isParticipant ===
            (this.user.profile['surveyparticipant'].toLowerCase() === 'true');
        }
        if (this.user.profile['surveyrespondent']) {
          this.isRespondent ===
            (this.user.profile['surveyrespondent'].toLowerCase() === 'true');
        }
      }
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.timeOfDaySubscription.unsubscribe();
    this.timeOfDaySubscription = Subscription.EMPTY;

    if (this.languageChangedSubscription) {
      this.languageChangedSubscription.unsubscribe();
      this.languageChangedSubscription = Subscription.EMPTY;
    }
  }

  notificationsClick(notification: Notification) {
    if (this.isLoading) {
      return;
    }

    this.notificationClicked.emit();
  }

  showProfile(): void {
    this.showProfileClicked.emit();
  }

  onNotificationView(notification: NotificationModel) {
    this.viewNotification.emit(notification);
  }

  onNotificationRemove(notification: NotificationModel) {
    this.removeNotification.emit(notification);
  }

  onDisclaimerCopyrightClick() {
    this.disclaimerCopyrightClick.emit();
  }

  private _getTimeKey() {
    let timeKey = '';
    const hour = moment().get('hour');
    if (hour < 12) {
      timeKey = 'todMorning';
    } else if (hour < 18) {
      timeKey = 'todAfternoon';
    } else {
      timeKey = 'todEvening';
    }
    return timeKey;
  }
}
